import React from 'react'
import { Table, Th, Td, Tr } from '@/shared/ui/Table'
import { Button, Input, Badge } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical, Search } from 'lucide-react'
import { Pagination } from '@/shared/ui/Pagination'
import type { Affiliate } from '../types'

type Props = {
  items: Affiliate[]
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  search: string
  onSearchChange: (v: string) => void
  onCreate: () => void
  onEdit: (a: Affiliate) => void
  onDelete: (a: Affiliate) => void
  onToggleActive: (a: Affiliate) => void
}

export default function AffiliatesTable({ items, total, page, pageSize, onPageChange, search, onSearchChange, onCreate, onEdit, onDelete, onToggleActive }: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const [q, setQ] = React.useState(search)
  React.useEffect(() => setQ(search), [search])
  React.useEffect(() => {
    const id = setTimeout(() => {
      if (q !== search) onSearchChange(q)
    }, 400)
    return () => clearTimeout(id)
  }, [q])
  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar afiliados" className="pl-9" />
        </div>
        <Button onClick={onCreate}>Novo afiliado</Button>
      </div>

      <Table>
        <thead>
          <Tr>
            <Th>Nome</Th>
            <Th>Email</Th>
            <Th>Código</Th>
            <Th>Data cadastro</Th>
            <Th>Status</Th>
            <Th className="w-12 text-right">Ações</Th>
          </Tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <Tr key={a.id}>
              <Td>{a.name ?? '—'}</Td>
              <Td>{a.email ?? '—'}</Td>
              <Td>{a.code ?? '—'}</Td>
              <Td>{a.created_at ? new Date(a.created_at).toISOString().replace('T',' ').slice(0,19) + ' UTC' : '—'}</Td>
              <Td>
                <Badge variant="soft" color={a.status === 'ativo' ? 'success' : 'error'} className="capitalize">
                  {a.status}
                </Badge>
              </Td>
              <Td className="text-right">
                <DropdownMenu
                  align="end"
                  trigger={<Button variant="outline" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    { label: 'Detalhes', onSelect: () => { window.location.assign(`/affiliates/${a.id}`) } },
                    { label: a.status === 'ativo' ? 'Suspender' : 'Ativar', onSelect: () => onToggleActive(a) },
                    { label: 'Editar', onSelect: () => onEdit(a) },
                    { label: <span className="text-destructive">Excluir</span>, onSelect: () => onDelete(a) },
                  ]}
                />
              </Td>
            </Tr>
          ))}
          {items.length === 0 && (
            <Tr>
              <Td colSpan={6} className="py-10 text-center text-sm text-foreground/70">Nenhum afiliado encontrado.</Td>
            </Tr>
          )}
        </tbody>
      </Table>

      <div className="mt-4 flex justify-end">
        <Pagination page={page} total={totalPages} onChange={onPageChange} />
      </div>
    </div>
  )
}
